import { PERMISSIONS, hasPermission } from '@helios/authz';
import { findDestinationById, userCanAccessDestination } from '@/navigation/registry';

export type FinanceArea =
  | 'overview'
  | 'cash'
  | 'receivables'
  | 'payables'
  | 'projects'
  | 'books'
  | 'controls';
export interface FinanceWorkspaceLink {
  label: string;
  to: string;
  destinationId?: string;
  permission?: string;
}
export const FINANCE_AREAS: ReadonlyArray<{ id: FinanceArea; label: string; to: string }> = [
  { id: 'overview', label: 'Overview', to: '/finance' },
  { id: 'cash', label: 'Cash & accounts', to: '/finance/cash' },
  { id: 'receivables', label: 'Receivables', to: '/finance/receivables' },
  { id: 'payables', label: 'Payables & costs', to: '/finance/payables' },
  { id: 'projects', label: 'Project finance', to: '/finance/projects' },
  { id: 'books', label: 'Books & reports', to: '/finance/books' },
  { id: 'controls', label: 'Controls', to: '/finance/controls' },
];
export const FINANCE_LINKS: Record<FinanceArea, readonly FinanceWorkspaceLink[]> = {
  overview: [{ label: 'Company overview', to: '/finance', destinationId: 'finance' }],
  cash: [
    { label: 'Book accounts', to: '/finance/accounts', destinationId: 'bank-accounts' },
    { label: 'Transactions', to: '/banking/transactions', destinationId: 'bank-transactions' },
    { label: 'Statements', to: '/banking/statements', destinationId: 'bank-statements' },
    {
      label: 'Reconciliation',
      to: '/banking/reconciliation',
      destinationId: 'bank-reconciliation',
    },
  ],
  receivables: [
    {
      label: 'Invoices',
      to: '/finance/receivables',
      permission: PERMISSIONS.FINANCE_AR_INVOICES_READ,
    },
    { label: 'Pay applications', to: '/finance/billing', destinationId: 'billing-applications' },
    { label: 'Collections', to: '/finance/collections', destinationId: 'billing-collections' },
    { label: 'Retainage', to: '/finance/retainage', destinationId: 'retainage-register' },
  ],
  payables: [
    { label: 'Bills', to: '/finance/payables', permission: PERMISSIONS.FINANCE_AP_INVOICES_READ },
    {
      label: 'Incoming bills',
      to: '/finance/payables/incoming',
      destinationId: 'incoming-bills',
    },
    {
      label: 'Recorded transaction activity',
      to: '/banking/spending',
      destinationId: 'bank-spending',
    },
  ],
  projects: [
    { label: 'Profitability & WIP', to: '/finance?view=profitability', destinationId: 'finance' },
    { label: 'Job cost', to: '/finance/job-cost-export', destinationId: 'job-cost-export' },
    { label: 'Analytics', to: '/finance/analytics', destinationId: 'finance-analytics' },
    {
      label: 'Budgets & actuals',
      to: '/projects/budget',
      permission: PERMISSIONS.FINANCE_BUDGETS_READ,
    },
  ],
  books: [
    { label: 'QuickBooks reports', to: '/accounting/reports', destinationId: 'quickbooks-reports' },
    { label: 'Native ledger', to: '/general-ledger', destinationId: 'general-ledger' },
    { label: 'Taxes & accountant', to: '/finance/taxes', destinationId: 'finance-taxes' },
  ],
  controls: [
    {
      label: 'QuickBooks source records',
      to: '/accounting/workspace',
      destinationId: 'accounting-workspace',
    },
    { label: 'Customers & vendors', to: '/finance/subjects', destinationId: 'finance-subjects' },
    { label: 'Mappings', to: '/banking/mappings', destinationId: 'accounting-mappings' },
    { label: 'Sync & coverage', to: '/banking/sync', destinationId: 'accounting-sync' },
  ],
};
export function canOpenFinanceLink(
  link: FinanceWorkspaceLink,
  permissions: readonly string[],
): boolean {
  if (link.destinationId) {
    const destination = findDestinationById(link.destinationId);
    return Boolean(destination && userCanAccessDestination(destination, permissions));
  }
  return Boolean(link.permission && hasPermission(permissions, link.permission));
}
export function financeAreaForLocation(path: string, search = ''): FinanceArea {
  if (/^\/finance\/(receivables|billing|collections|retainage)(\/|$)/.test(path))
    return 'receivables';
  if (/^\/finance\/payables(\/|$)/.test(path) || path === '/banking/spending') return 'payables';
  if (
    /^\/finance\/(books|taxes)(\/|$)/.test(path) ||
    path === '/general-ledger' ||
    path === '/accounting/reports'
  )
    return 'books';
  if (
    /^\/finance\/(controls|subjects)(\/|$)/.test(path) ||
    path === '/accounting/workspace' ||
    /^\/banking\/(mappings|sync)$/.test(path)
  )
    return 'controls';
  if (/^\/finance\/(projects|analytics|job-cost-export)(\/|$)/.test(path)) return 'projects';
  if (/^\/(banking|finance\/(cash|accounts))(\/|$)/.test(path)) return 'cash';
  const params = new URLSearchParams(search);
  if (params.get('view') === 'profitability') return 'projects';
  if (params.get('view') === 'detail')
    return params.get('invoiceDirection') === 'ap' ? 'payables' : 'receivables';
  return 'overview';
}
